type CapturedProperties = Record<string, unknown>;

function isUrl(value: unknown): value is string {
  return (
    typeof value === "string" &&
    (value.startsWith("https://") || value.startsWith("http://"))
  );
}

function cleanValue(value: unknown): unknown {
  return isUrl(value) ? value.split(/[?#]/, 1)[0] : value;
}

/**
 * Removes the query string and fragment from every URL in a set of properties,
 * returning the original object when there was nothing to remove.
 *
 * The app reports the URLs it names itself already trimmed, but the SDK adds
 * its own from `window.location` for events the app never asked for. The
 * sign-in callback carries a one-time code in the query string, and other
 * flows put tokens in the fragment, so neither leaves the browser on any
 * event.
 *
 * Every URL-shaped value is cleaned rather than a list of known property
 * names, because the SDK adds new ones over time and a name we have not heard
 * of yet is exactly the one that would leak.
 */
export function stripQueryStrings(
  properties: CapturedProperties,
): CapturedProperties {
  let cleaned: CapturedProperties | null = null;

  for (const [key, value] of Object.entries(properties)) {
    const replacement =
      (key === "$set" || key === "$set_once") && isPlainObject(value)
        ? stripQueryStrings(value)
        : cleanValue(value);
    if (replacement === value) continue;
    cleaned ??= { ...properties };
    cleaned[key] = replacement;
  }

  return cleaned ?? properties;
}

function isPlainObject(value: unknown): value is CapturedProperties {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** An event as the SDK hands it to the send hook, reduced to what is cleaned. */
interface CapturedEvent {
  properties: CapturedProperties;
  $set?: CapturedProperties;
  $set_once?: CapturedProperties;
}

/**
 * Cleans an outgoing event, event properties and person properties alike.
 *
 * The person properties matter most: `$initial_*` is captured once, on the
 * first page anyone lands on, and resent with later events. A sign-in callback
 * is a very common first page, so leaving those untouched would preserve a
 * one-time code long after the visit that made it.
 *
 * Runs on every event, replay snapshots included, so it allocates only when a
 * value actually changes.
 */
export function stripEventQueryStrings<T extends CapturedEvent>(event: T): T {
  const properties = stripQueryStrings(event.properties);
  const $set = event.$set && stripQueryStrings(event.$set);
  const $set_once = event.$set_once && stripQueryStrings(event.$set_once);

  // `title` is document.title, a constant here and useless, but one
  // well-meaning change away from being the name of an invention.
  const hasTitle = "title" in properties;
  if (
    !hasTitle &&
    properties === event.properties &&
    $set === event.$set &&
    $set_once === event.$set_once
  ) {
    return event;
  }

  const { title: _title, ...withoutTitle } = properties;
  return {
    ...event,
    properties: hasTitle ? withoutTitle : properties,
    ...($set ? { $set } : {}),
    ...($set_once ? { $set_once } : {}),
  };
}
